import { Observable, zip } from 'rxjs';
import { map, retry, share } from 'rxjs/operators';
import { AppContext } from '../data/app-context';
import { QueryCache } from '../data/query-cache';
import { ClusterInventory } from '../shared/cluster-inventory/cluster-inventory';
import { ClusterInventoryCache } from '../shared/cluster-inventory/cluster-inventory-cache';
import { GatewayInventory } from '../shared/gateway-inventory/gateway-inventory';
import { GatewayInventoryCache } from '../shared/gateway-inventory/gateway-inventory-cache';
import { GatewayInventoryDetail } from '../shared/gateway-inventory/gateway-inventory-detail';
import { GatewayInventoryDetailCache } from '../shared/gateway-inventory/gateway-inventory-detail-cache';
import { ServerInventory } from '../shared/server-inventory/server-inventory';
import { ServerInventoryCache } from '../shared/server-inventory/server-inventory-cache';
import { ServerInventoryDetail } from '../shared/server-inventory/server-inventory-detail';
import { ServerInventoryDetailCache } from '../shared/server-inventory/server-inventory-detail-cache';
import { SharedCacheOptions } from './shared-cache';

export interface InventoryQueryCachesParams {
    name?: string;
    retry?: number;
}

export interface InventoryQueryCachesOptions {
    serverCache?: {
        expiration?: number
    };
    serverCombinedCache?: {
        expiration?: number
    };
    gatewayCache?: {
        expiration?: number
    };
    gatewayCombinedCache?: {
        expiration?: number
    };
    clusterCache?: {
        expiration?: number
    };
}

export class InventoryQueryCaches {
    private serverInventoryCache: ServerInventoryCache;
    private serverInventoryDetailCache: ServerInventoryDetailCache;
    private gatewayInventoryCache: GatewayInventoryCache;
    private gatewayInventoryDetailCache: GatewayInventoryDetailCache;
    private clusterInventoryCache: ClusterInventoryCache;

    private serverInventorySharedContext = {
        name: <string>null,
        server: false,
        detail: false,
        shared: <Observable<ServerInventory>>null
    };

    private serverInventoryCacheNodeName: string;
    private gatewayCached = false;
    private gatewayCombinedCached = false;

    public serverCache: QueryCache<ServerInventory, InventoryQueryCachesParams>;
    public serverCombinedCache: QueryCache<[ServerInventory, ServerInventoryDetail], InventoryQueryCachesParams>;
    public gatewayCache: QueryCache<GatewayInventory, InventoryQueryCachesParams>;
    public gatewayCombinedCache: QueryCache<[GatewayInventory, GatewayInventoryDetail], InventoryQueryCachesParams>;
    public clusterCache: QueryCache<ClusterInventory, InventoryQueryCachesParams>;

    constructor(private appContext: AppContext, options?: InventoryQueryCachesOptions) {
        let cacheOptions: SharedCacheOptions;
        if (options.serverCache) {
            cacheOptions = options.serverCache.expiration ? { expiration: options.serverCache.expiration } : null;
            this.serverInventoryCache = new ServerInventoryCache(this.appContext, cacheOptions);
            this.serverCache = new QueryCache<ServerInventory, InventoryQueryCachesParams>(
                params => this.createServerInventory(params),
                params => params.name);
        }

        if (options.serverCache && options.serverCombinedCache) {
            cacheOptions = options.serverCombinedCache.expiration ? { expiration: options.serverCombinedCache.expiration } : null;
            this.serverInventoryDetailCache = new ServerInventoryDetailCache(this.appContext, cacheOptions);
            this.serverCombinedCache = new QueryCache<[ServerInventory, ServerInventoryDetail], InventoryQueryCachesParams>(
                params => this.createServerInventoryCombined(params),
                params => params.name,
                () => this.serverInventoryCacheNodeName = null);
        }

        if (options.gatewayCache) {
            cacheOptions = options.gatewayCache.expiration ? { expiration: options.gatewayCache.expiration } : null;
            this.gatewayInventoryCache = new GatewayInventoryCache(this.appContext, cacheOptions);
            this.gatewayCache = new QueryCache<GatewayInventory, InventoryQueryCachesParams>(
                params => this.createGatewayInventory(params),
                () => 'gateway',
                () => this.gatewayCached = false);
        }

        if (options.gatewayCache && options.gatewayCombinedCache) {
            cacheOptions = options.gatewayCombinedCache.expiration ? { expiration: options.gatewayCombinedCache.expiration } : null;
            this.gatewayInventoryDetailCache = new GatewayInventoryDetailCache(this.appContext, cacheOptions);
            this.gatewayCombinedCache = new QueryCache<[GatewayInventory, GatewayInventoryDetail], InventoryQueryCachesParams>(
                params => this.createGatewayInventoryCombined(params),
                () => 'gateway',
                () => this.gatewayCombinedCached = false);
        }

        if (options.clusterCache) {
            cacheOptions = options.clusterCache.expiration ? { expiration: options.clusterCache.expiration } : null;
            this.clusterInventoryCache = new ClusterInventoryCache(this.appContext, cacheOptions);
            this.clusterCache = new QueryCache<ClusterInventory, InventoryQueryCachesParams>(
                params => this.createClusterInventory(params),
                params => params.name);
        }
    }

    /**
     * Clear all session storage.
     */
    public clear(): void {
        if (this.serverInventoryCache) {
            this.serverInventoryCache.clear();
        }
        if (this.serverInventoryDetailCache) {
            this.serverInventoryDetailCache.clear();
        }
        if (this.gatewayInventoryCache) {
            this.gatewayInventoryCache.clear();
        }
        if (this.gatewayInventoryDetailCache) {
            this.gatewayInventoryDetailCache.clear();
        }
        if (this.clusterInventoryCache) {
            this.clusterInventoryCache.clear();
        }
    }

    /**
     * Create server inventory query.
     * - It shares the query with ServerInventory and ServerInventoryCombined. Keep track the request and re-create if asked twice.
     *
     * @param params the inventory query parameters.
     * @param detail the state of server query or detail query.
     * @return Observable<ServerInventory> Shared ServerInventory observable.
     */
    private createServerInventory(params: InventoryQueryCachesParams, detail?: boolean): Observable<ServerInventory> {
        // check
        // - if not created yet.
        // - if requested server name is changed.
        // - if server query second time.
        // - if detail query second time.
        const createNew = !this.serverInventorySharedContext.shared
            || params.name !== this.serverInventorySharedContext.name
            || (this.serverInventorySharedContext.server && !detail)
            || (this.serverInventorySharedContext.detail && detail);

        if (!createNew) {
            if (detail) {
                this.serverInventorySharedContext.detail = true;
            } else {
                this.serverInventorySharedContext.server = true;
            }

            // return shared pre-created one.
            return this.serverInventorySharedContext.shared;
        }

        // creates new shared observable.
        let observable = this.serverInventoryCache.query({ name: params.name })
            .pipe(map(inventory => inventory.instance));
        if (params && params.retry) {
            observable = observable.pipe(retry(params.retry));
        }

        this.serverInventorySharedContext = { name: params.name, server: !detail, detail: !!detail, shared: observable.pipe(share()) };
        return this.serverInventorySharedContext.shared;
    }

    private createServerInventoryCombined(params: InventoryQueryCachesParams): Observable<[ServerInventory, ServerInventoryDetail]> {
        let observable: Observable<[ServerInventory, ServerInventoryDetail]>;
        if (!this.serverInventoryCacheNodeName || this.serverInventoryCacheNodeName !== params.name) {
            this.serverInventoryCacheNodeName = params.name;
            observable = zip(
                this.createServerInventory(params, true),
                this.serverInventoryDetailCache.query({ name: params.name })
                    .pipe(map(inventory => inventory.instance)));
        } else {
            observable = zip(
                this.serverInventoryCache.refresh({ name: params.name })
                    .pipe(map(inventory => inventory.instance)),
                this.serverInventoryDetailCache.refresh({ name: params.name })
                    .pipe(map(inventory => inventory.instance)));
        }

        if (params && params.retry) {
            return observable.pipe(retry(params.retry));
        }

        return observable;
    }

    private createGatewayInventory(params: InventoryQueryCachesParams): Observable<GatewayInventory> {
        let observable: Observable<GatewayInventory>;
        if (!this.gatewayCached) {
            this.gatewayCached = true;
            observable = this.gatewayInventoryCache.query({}).pipe(map(inventory => inventory.instance));
        } else {
            observable = this.gatewayInventoryCache.refresh({}).pipe(map(inventory => inventory.instance));
        }

        if (params && params.retry) {
            return observable.pipe(retry(params.retry));
        }

        return observable;
    }

    private createGatewayInventoryCombined(params: InventoryQueryCachesParams): Observable<[GatewayInventory, GatewayInventoryDetail]> {
        let observable: Observable<[GatewayInventory, GatewayInventoryDetail]>;
        if (!this.gatewayCombinedCached) {
            this.gatewayCombinedCached = true;
            observable = zip(
                this.gatewayInventoryCache.query({}).pipe(map(inventory => inventory.instance)),
                this.gatewayInventoryDetailCache.query({}).pipe(map(inventory => inventory.instance)));
        } else {
            observable = zip(
                this.gatewayInventoryCache.refresh({}).pipe(map(inventory => inventory.instance)),
                this.gatewayInventoryDetailCache.refresh({}).pipe(map(inventory => inventory.instance)));
        }

        if (params && params.retry) {
            return observable.pipe(retry(params.retry));
        }

        return observable;
    }

    private createClusterInventory(params: InventoryQueryCachesParams): Observable<ClusterInventory> {
        const observable = this.clusterInventoryCache.query({ name: params.name })
            .pipe(map(inventory => inventory.instance));
        if (params && params.retry) {
            return observable.pipe(retry(params.retry));
        }

        return observable;
    }
}
